import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Plus, Trash2, Edit2 } from "lucide-react";

export default function AdminArticlesPage() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState({ type: "", status: "", search: "" });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter.type) params.set("type", filter.type);
      if (filter.status) params.set("status", filter.status);
      if (filter.search) params.set("search", filter.search);
      const { data } = await api.get(`/admin/articles?${params}`);
      setItems(data.items || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter.type, filter.status]);

  const onNew = async () => {
    const { data } = await api.post("/admin/articles", {
      type: "journal", status: "draft",
      translations: { tr: { title: "Başlıksız taslak", enabled: true } },
    });
    navigate(`/admin/articles/${data.id}`);
  };

  const onDelete = async (id) => {
    if (!window.confirm("Bu yazıyı arşivlemek istediğinize emin misiniz?")) return;
    await api.delete(`/admin/articles/${id}`);
    load();
  };

  return (
    <div data-testid="admin-articles">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif text-4xl text-cc-text mb-2">İçerikler</h1>
          <p className="text-sm text-cc-muted">Tüm makaleleri ve rehberleri yönetin</p>
        </div>
        <button onClick={onNew} className="cc-btn" data-testid="new-article-btn">
          <Plus size={14} /> Yeni Yazı
        </button>
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <select value={filter.type} onChange={(e) => setFilter({ ...filter, type: e.target.value })} className="bg-white border border-admin-border px-3 py-2 text-sm" data-testid="filter-type">
          <option value="">Tüm türler</option>
          <option value="journal">Journal</option>
          <option value="guide">Rehber</option>
        </select>
        <select value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })} className="bg-white border border-admin-border px-3 py-2 text-sm" data-testid="filter-status">
          <option value="">Tüm durumlar</option>
          <option value="draft">Taslak</option>
          <option value="published">Yayında</option>
          <option value="archived">Arşivlenmiş</option>
        </select>
        <input placeholder="Ara…" value={filter.search} onChange={(e) => setFilter({ ...filter, search: e.target.value })}
               onKeyDown={(e) => e.key === "Enter" && load()}
               className="bg-white border border-admin-border px-3 py-2 text-sm flex-1 min-w-[200px]" data-testid="filter-search" />
      </div>

      <div className="bg-white border border-admin-border" data-testid="articles-table">
        <table className="w-full text-sm">
          <thead className="bg-admin-bg/60 text-left">
            <tr className="border-b border-admin-border">
              <th className="px-4 py-3 text-[10px] tracking-overline uppercase text-cc-muted">Başlık</th>
              <th className="px-4 py-3 text-[10px] tracking-overline uppercase text-cc-muted">Tür</th>
              <th className="px-4 py-3 text-[10px] tracking-overline uppercase text-cc-muted">Durum</th>
              <th className="px-4 py-3 text-[10px] tracking-overline uppercase text-cc-muted">Güncellenme</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan="5" className="px-4 py-6 text-cc-muted">Yükleniyor…</td></tr>
              : items.length === 0 ? <tr><td colSpan="5" className="px-4 py-6 text-cc-muted">Yazı bulunamadı.</td></tr>
              : items.map((a) => (
                <tr key={a.id} className="border-b border-admin-border last:border-0 hover:bg-admin-bg/40">
                  <td className="px-4 py-3">
                    <Link to={`/admin/articles/${a.id}`} className="text-cc-text hover:text-cc-sepia">
                      {a.translations?.tr?.title || "(başlıksız)"}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-cc-muted">{a.type}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] uppercase tracking-overline px-2 py-1 ${a.status === "published" ? "bg-cc-sepia/15 text-cc-sepia" : "bg-cc-border/50 text-cc-muted"}`}>{a.status}</span>
                  </td>
                  <td className="px-4 py-3 text-cc-muted text-xs">{a.updated_at ? new Date(a.updated_at).toLocaleDateString("tr-TR") : "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <Link to={`/admin/articles/${a.id}`} className="inline-block text-cc-muted hover:text-cc-sepia mr-3" data-testid={`edit-${a.id}`}><Edit2 size={14} /></Link>
                    <button onClick={() => onDelete(a.id)} className="text-cc-muted hover:text-red-600" data-testid={`delete-${a.id}`}><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
